import React, { useState, useEffect, useRef, useCallback } from "react";
import { StyleSheet, View, SafeAreaView, useWindowDimensions, FlatList, ActivityIndicator } from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { useTranslation } from "react-i18next";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import { formatDateForUI } from "../../../constants/FormatDate";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { useTableHelper } from "../../../hooks/useTableHelper";
import { showMessage } from "react-native-flash-message";
import { v4 as uuidv4 } from 'uuid';

const EmplProfBodiesListView = React.memo(
    ({
        item,
        index,
        handleEditPress,
        styles,
        profBodiesHelper,
        t,
        handleDelete,
        resetDates,
        openCalendar,
    }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [showCalendarDoa, setShowCalendarDoa] = useState(false);

        const toggleExpand = () => {
            setIsExpanded(!isExpanded);
        };

        return (
            <View>
                <SafeAreaView>
                    <SafeAreaView style={styles.row}>
                        <View style={[styles.item]}>
                            <CustomDropDown
                                defValue={item.profBody}
                                options={profBodiesHelper.ProfBodies}
                                value={item.profBody}
                                setValue={(e) =>
                                    handleEditPress(index, item, "profBody", e)
                                }
                                type="FULL"
                            />
                        </View>
                        <View style={[styles.item]}>
                            <CustomDropDown
                                defValue={item.mType}
                                options={profBodiesHelper.MTypes}
                                value={item.mType}
                                setValue={(e) =>
                                    handleEditPress(index, item, "mType", e)
                                }
                                type="FULL"
                            />
                        </View>
                        <View style={[styles.item]}>
                            <SafeAreaView style={[styles.row]}>
                                <SafeAreaView>
                                    <CustomInput
                                        value={formatDateForUI(item.dor)}
                                        setValue={() => { }}
                                        maxLen={50}
                                        editable={false}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <Ionicons
                                    name={showCalendarDoa ? "calendar" : "calendar-outline"}
                                    style={styles.icons}
                                    onPress={() => openCalendar('dor', item.dor)}
                                />
                            </SafeAreaView>
                        </View>
                        <View style={[styles.item]}>
                            <CustomInput
                                value={item.memNumber}
                                setValue={(text) => {
                                    handleEditPress(index, item, "memNumber", text);
                                }}
                                editable={true}
                                maxLen={50}
                                type="MAININPUT"
                            />
                        </View>
                        <SafeAreaView>
                            <CustomButton
                                iconName="trash-outline"
                                onPress={() => handleDelete(item, index, 'rowguid')}
                                show="HOME_BELL"
                                type="HOME_BELL"
                                textTop="2"
                                sz={30}
                            />
                            <Ionicons
                                name={
                                    isExpanded
                                        ? "chevron-down-outline"
                                        : "chevron-forward-outline"
                                }
                                style={styles.icons}
                                onPress={toggleExpand}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                    {isExpanded && (
                        <>
                            <SafeAreaView style={[styles.row2, { marginRight: 40 }]}>
                                <SafeAreaView style={[styles.row]}>
                                    <SafeAreaView>
                                        <CustomLabel value={t("common:examdate")} type="SUBTITLE" />
                                        <CustomInput
                                            value={formatDateForUI(item.dofe)}
                                            setValue={() => { }}
                                            maxLen={50}
                                            editable={false}
                                            type="MAININPUT"
                                        />
                                    </SafeAreaView>
                                    <Ionicons
                                        name={showCalendarDoa ? "calendar" : "calendar-outline"}
                                        style={styles.icons}
                                        onPress={() => openCalendar('dofe', item.dofe)}
                                    />
                                </SafeAreaView>
                                <SafeAreaView style={[styles.row]}>
                                    <SafeAreaView>
                                        <CustomLabel value={t("common:expdate")} type="SUBTITLE" />
                                        <CustomInput
                                            value={formatDateForUI(item.doe)}
                                            setValue={() => { }}
                                            maxLen={50}
                                            editable={false}
                                            type="MAININPUT"
                                        />
                                    </SafeAreaView>
                                    <Ionicons
                                        name={showCalendarDoa ? "calendar" : "calendar-outline"}
                                        style={styles.icons}
                                        onPress={() => openCalendar('doe', item.doe)}
                                    />
                                </SafeAreaView>
                            </SafeAreaView>
                        </>
                    )}
                </SafeAreaView>
            </View>
        );
    }
);

const HRAdminEmplProBodies = (props) => {
    const { width } = useWindowDimensions();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width);
    const [changes, setChanges] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [profBodiesHelper, setProfBodiesHelper] = useState({});
    const [profBodies, setProfBodies] = useState([]);
    const { t } = useTranslation();
    const ref = useRef(null);
    const { GetEmplProfBodiesHelper, GetEmplProfBodies, SaveEmplProfBodies, deleteEmplProfBodies, isLoading } = useHRAdminEmployees();
    const { rowAdd, rowEdit, rowDelete, scrollToTop } = useTableHelper();
    const [resetDates, setResetDates] = useState([false]);
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [calendarFieldName, setCalendarFieldName] = useState('');
    const [calendarItemIndex, setCalendarItemIndex] = useState(null);
    const [calendarSelectedData, setCalendarSelectedData] = useState({ startDate: '', endDate: '' });

    useEffect(() => {
        if (props.selectedEmployee?.employeeID) {
            loadData();
        }
    }, [props.selectedEmployee]);

    const loadData = () => {
        GetEmplProfBodiesHelper()
            .then((helper) => {
                setProfBodiesHelper(helper);
                return GetEmplProfBodies(props.selectedEmployee.employeeID);
            })
            .then((bodies) => {
                setProfBodies(bodies);
                setOriginalData(bodies);
            })
            .catch((error) => {
                console.error('Error in loadData:', error);
            });
    };

    const getValidDateOrToday = (dateStr) => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        const dateOnly = dateStr.split('T')[0];
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (isoDateRegex.test(dateOnly)) {
            return dateOnly;
        }
        return new Date().toISOString().split('T')[0];
    };

    const openCalendarForItem = (index, fieldName, currentDate) => {
        const validDate = getValidDateOrToday(currentDate);
        setCalendarItemIndex(index);
        setCalendarFieldName(fieldName);
        setCalendarSelectedData({ startDate: validDate, endDate: validDate });
        setCalendarVisible(true);
    };

    const closeCalendar = () => {
        setCalendarVisible(false);
        setCalendarItemIndex(null);
        setCalendarFieldName('');
    };

    const onCalendarDateChange = (newDates) => {
        setCalendarSelectedData(newDates);
        if (calendarItemIndex !== null) {
            handleEditPress(calendarItemIndex, profBodies[calendarItemIndex], calendarFieldName, newDates.startDate);
        }
        closeCalendar();
    };

    const handleEditPress = useCallback(
        async (index, item, key, value) => {
            if (item[key] === value) {
                return;
            }
            rowEdit(
                index,
                item,
                key,
                value,
                changes,
                setChanges,
                profBodies,
                setProfBodies
            );
        },
        [rowEdit, changes, profBodies, setProfBodies]
    );

    const handleCancelChanges = () => {
        const rolledBack = originalData.map((obj) => ({ ...obj }));
        setProfBodies(rolledBack);
        setDeletedItems([]);
        setChanges({});
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
        setResetDates(true);
    };

    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')]
        profBodies.forEach((itr) => {
            if (!itr.profBody)
                ret = [false, t('common:proEmpty')]
            else if (!itr.mType)
                ret = [false, t('common:memtypeEmpty')]
        })
        return (ret)
    }

    const handleSave = async () => {
        try {
            if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
                showMessage({
                    message: t('common:noChanges'),
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let valRet = validateInput();
            if (!valRet[0]) {
                showMessage({
                    message: valRet[1],
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let savePromises = [];
            let updateSucc = true;
            let deleteSucc = true;

            if (Object.entries(changes).length > 0) {
                savePromises.push(
                    SaveEmplProfBodies(
                        changes,
                        props?.selectedEmployee?.employeeID
                    ).then((resp) => {
                        updateSucc = resp;
                    }).catch((error) => {
                        console.error("Save error", error.response?.data ?? error.message);
                        updateSucc = false;
                    })
                );
            }

            if (deletedItems.length > 0) {
                savePromises.push(deleteEmplProfBodies(deletedItems)
                    .then((resp) => {
                        deleteSucc = resp;
                        removeFromChangesAfterSave();
                    }).catch((error) => {
                        console.error('delete failed', error.response?.data ?? error.message);
                        deleteSucc = false;
                    }))
            }

            await Promise.all(savePromises);

            if (!updateSucc || !deleteSucc) {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
                loadData();
                return;
            }


            removeFromChangesAfterSave();
            setResetDates(true);
            showMessage({
                message: t("common:profBodiesSuccessSave"),
                type: "success",
                duration: 3000,
            });
            loadData();
            
        } catch (error) {
            showMessage({
                message: t("common:errorSave"),
                type: "danger",
                duration: 3000,
            });
        }
    };

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, profBodies, setProfBodies, setDeletedItems, ref)
    }

    const handleAddNewRow = () => {
        if (!Array.isArray(profBodies) || profBodies.length === 0) {
            const newItem = {
                rowguid: uuidv4(),
                employeeId: props?.selectedEmployee?.employeeID,
                profBody: '',
                mType: '',
                dor: null,
                dofe: '',
                doe: '',
                memNumber: '',
            };

            setProfBodies([newItem]);
            setChanges({ 0: { ...newItem } });
            scrollToTop(ref);
            return;
        }

        rowAdd(
            profBodies,
            setProfBodies,
            ['rowguid', 'employeeId'],
            [],
            ref,
            t,
            ['employeeId'],
            [],
            changes,
            setChanges,
            undefined,
            undefined,
            "rowguid",
            false,
            ['dor'],
        );
    };

    const removeFromChangesAfterSave = () => {
        setDeletedItems([]);
        setChanges({});
    };

    const renderProfBodies = useCallback(
        ({ item, index }) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <EmplProfBodiesListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        profBodiesHelper={profBodiesHelper}
                        styles={styles}
                        t={t}
                        handleDelete={handleDelete}
                        resetDates={resetDates}
                        openCalendar={(fieldName, currentDate) => openCalendarForItem(index, fieldName, currentDate)}
                    />
                </Card.Content>
            </Card>
        ),
        [
            profBodies,
            handleEditPress,
            handleDelete,
            styles,
            resetDates,
        ]
    );

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={styles.container}>
                <View style={[styles.buttons, styles.functionalityButtons]}>
                    <CustomButton
                        text={t("common:new")}
                        onPress={handleAddNewRow}
                        type="BUTTON_ADD"
                        textTop="0"
                        sz={15}
                    />
                    {(Object.keys(changes).length > 0 ||
                        changes.length > 0 || deletedItems.length > 0) && (
                            <CustomButton
                                text={t("common:saveFunction")}
                                onPress={handleSave}
                                type="BUTTON_SAVE"
                                isGradient={true}
                                textTop="0"
                                sz={15}
                            />
                        )}
                    <CustomButton
                        text={t("common:cancel")}
                        onPress={handleCancelChanges}
                        type="BUTTON_CANCEL"
                        textTop="0"
                        sz={15}
                    />
                </View>
                <FlatList
                    data={profBodies}
                    keyExtractor={(item, index) => `${item.rowguid}-${index}`}
                    renderItem={renderProfBodies}
                    refreshing={isLoading}
                    ref={ref}
                    ListHeaderComponent={
                        <SafeAreaView style={common.listTitle}>
                            <SafeAreaView style={styles.row}>
                                <CustomLabel value={t("common:pro")} type="SUBTITLE" />
                                <CustomLabel value={t("common:memtype")} type="SUBTITLE" />
                                <CustomLabel value={t("common:memdate")} type="SUBTITLE" />
                                <CustomLabel value={t("common:reginumber")} type="SUBTITLE" />
                                <SafeAreaView style={[{ marginRight: '5%' }]} />
                            </SafeAreaView>
                            <SafeAreaView style={common.divider} />
                        </SafeAreaView>
                    }
                    ListEmptyComponent={
                        isLoading ? (
                            <ActivityIndicator animating={true} color={colors.blue} />
                        ) : (
                            <CustomLabel
                                value={t("common:noReviewDataPreview")}
                                type="NO_FUNCTIONS"
                            />
                        )
                    }
                    initialNumToRender={4}
                />
                {calendarVisible && (
                    <>
                        <View
                            style={styles.overlay}
                            onStartShouldSetResponder={() => true}
                            onResponderRelease={closeCalendar}
                        />
                        <SafeAreaView style={styles.calendarOverlay}>
                            <CustomCalendar
                                calendarType={"RangePicker"}
                                setRange={onCalendarDateChange}
                                range={calendarSelectedData}
                                allowSingleDate={true}
                                startingYear={1950}
                            />
                        </SafeAreaView>
                    </>
                )}
            </View>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) =>
    StyleSheet.create({
        mainView: {
            flex: 1
        },
        container: {
            flex: 1
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center"
        },
        row2: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
            margin: 10
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6
        },
        buttons: {
            flexDirection: "row",
            justifyContent: "flex-end",
            alignSelf: "flex-end",
            marginRight: 15
        },
        functionalityButtons: {
            justifyContent: "flex-end"
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%"
        },
        icons: {
            fontSize: 24,
            marginHorizontal: 10
        },
        calendarOverlay: {
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: [{ translateX: -160 }],
            backgroundColor: 'white',
            borderRadius: 10,
            zIndex: 999,
            maxHeight: '90%',
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 6 },
            shadowOpacity: 0.5,
            shadowRadius: 20,
            elevation: 15,
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
        },
        overlay: {
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
        },
    });

export default HRAdminEmplProBodies;